"use client";
import React, {
  memo,
  // useCallback,
  useEffect,
  // useId,
  useState,
  useRef,
} from "react";

const VISIBILITY_DEBOUNCE_MS = 200;
// import Script from "next/script";
import { Container } from "@repo/ui/Container";
import { Section, SectionProps } from "@repo/ui/Section";
import { cn } from "@repo/utils/cn";
import { VariantProps } from "tailwind-variants";

import { ImageWithAspectRatioProps } from "@repo/ui/global";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "../AiDisclaimer";
import { SelectOption } from "@repo/ui/Select";
import { Button, ButtonProps } from "../Button";
import { Input } from "../Input";
import { Textarea } from "../Textarea";
import { Select } from "../Select";
// import { PhoneNumberSelect } from "../PhoneNumberSelect"; // Commented out for US/Canada only (+1) support
import { PhoneInput } from "../PhoneInput";
import { Checkbox } from "../Checkbox";
import { DatePicker } from "../DatePicker";
import { Box } from "@repo/ui/Box";
import { SectionTitle, SectionTitleProps } from "../SectionTitle";
import { sectionWrapper } from "../sectionWrappers";

const SectionTitleMemo = memo(SectionTitle);

type FormFieldProps = {
  field: ContactFormField;
  formValues: Record<string, string | number>;
  formErrors: FormErrors;
  phoneData: Record<string, { countryCode: string; phoneNumber: string }>;
  inputBackgroundColor: "default" | "white";
  onFieldChange: (fieldName: string, value: string) => void;
  onPhoneChange: (
    fieldName: string,
    countryCode: string,
    phoneNumber: string,
  ) => void;
  onFieldFocus: (fieldName: string) => void;
  onFieldBlur: (fieldName: string) => void;
};

const FormField = memo(
  ({
    field,
    formValues,
    formErrors,
    phoneData,
    inputBackgroundColor,
    onFieldChange,
    onPhoneChange,
    onFieldFocus,
    onFieldBlur,
  }: FormFieldProps) => {
    if (!field.name) return null;

    const fieldValue = formValues[field.name] || "";
    const fieldError = formErrors[field.name];

    const inputBgVariant =
      inputBackgroundColor === "white" ? "white" : "default";

    switch (field.type) {
      case "select":
        return (
          <div {...field} className="uipm:w-full">
            <Select
              id={field.name}
              placeholder={field.placeholder || "Placeholder"}
              state={fieldError ? "error" : "default"}
              value={String(fieldValue)}
              background={inputBgVariant}
              onChange={(e) =>
                onFieldChange(field.name, (e.target as HTMLSelectElement).value)
              }
            >
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
        );
      case "tel":
        return (
          <div {...field} className="uipm:w-full">
            {/* Commented out PhoneNumberSelect for US/Canada only (+1) support - can be restored later if needed */}
            {/* <PhoneNumberSelect
              key={field.name}
              id={field.name}
              name={field.name}
              placeholder={field.placeholder || "Placeholder"}
              options={field.options || []}
              value={String(fieldValue)}
              state={fieldError ? "error" : "default"}
              errorMessage={fieldError || undefined}
              background={inputBgVariant}
              onChange={(countryCode, phoneNumber) =>
                onPhoneChange(field.name!, countryCode, phoneNumber)
              }
              onBlur={() => onFieldBlur(field.name)}
            /> */}

            {/* Using PhoneInput with fixed +1 for US/Canada */}
            <PhoneInput
              key={field.name}
              id={field.name}
              name={field.name}
              placeholder={field.placeholder || "Placeholder"}
              value={String(fieldValue)}
              state={fieldError ? "error" : "default"}
              background={inputBgVariant}
              onFocus={() => onFieldFocus(field.name)}
              onChange={(e) => {
                // Store full phone with +1 prefix
                onFieldChange(field.name, e.target.value);
              }}
              onBlur={() => onFieldBlur(field.name)}
            />
          </div>
        );
      case "textarea":
        return (
          <div {...field} className="uipm:w-full">
            <Textarea
              id={field.name}
              name={field.name}
              placeholder={field.placeholder || "Placeholder"}
              value={String(fieldValue)}
              state={fieldError ? "error" : "default"}
              background={inputBgVariant}
              onFocus={() => onFieldFocus(field.name)}
              onChange={(e) => onFieldChange(field.name, e.target.value)}
              onBlur={() => onFieldBlur(field.name)}
            />
          </div>
        );
      case "date":
        return (
          <div {...field} className="uipm:w-full">
            <DatePicker
              key={Object.keys(formValues).length === 0 ? "reset" : "active"}
              id={field.name}
              name={field.name!}
              placeholderText={field.placeholder || "Select date"}
              defaultValue={
                fieldValue
                  ? {
                      startDate: new Date(String(fieldValue)),
                      endDate: null,
                      startTime: null,
                      endTime: null,
                    }
                  : undefined
              }
              state={fieldError ? "error" : "default"}
              background={inputBgVariant}
              onFocus={() => onFieldFocus(field.name)}
              onChange={(value) => {
                const dateValue: string = value.startDate
                  ? value.startDate.toISOString().substring(0, 10)
                  : "";
                onFieldChange(field.name ?? "", dateValue);
              }}
              onBlur={() => onFieldBlur(field.name!)}
            />
          </div>
        );
      default:
        return (
          <div {...field} className="uipm:w-full">
            <Input
              id={field.name}
              type={field.type === "email" ? "email" : "text"}
              name={field.name}
              placeholder={field.placeholder || "Placeholder"}
              value={String(fieldValue)}
              state={fieldError ? "error" : "default"}
              background={inputBgVariant}
              onFocus={() => onFieldFocus(field.name)}
              onChange={(e) => onFieldChange(field.name, e.target.value)}
              onBlur={() => onFieldBlur(field.name)}
            />
          </div>
        );
    }
  },
);

type FormFieldGroupProps = {
  group: ContactFormInputGroup;
  instanceId?: string;
  formValues: Record<string, string | number>;
  formErrors: FormErrors;
  phoneData: Record<string, { countryCode: string; phoneNumber: string }>;
  inputBackgroundColor: "default" | "white";
  onFieldChange: (fieldName: string, value: string) => void;
  onPhoneChange: (
    fieldName: string,
    countryCode: string,
    phoneNumber: string,
  ) => void;
  onFieldFocus: (fieldName: string) => void;
  onFieldBlur: (fieldName: string) => void;
  onAddMore?: () => void;
  onRemove?: () => void;
  isDuplicate?: boolean;
};

const FormFieldGroup = memo(
  ({
    group,
    instanceId,
    formValues,
    formErrors,
    phoneData,
    inputBackgroundColor,
    onFieldChange,
    onPhoneChange,
    onFieldFocus,
    onFieldBlur,
    onAddMore,
    onRemove,
    isDuplicate,
  }: FormFieldGroupProps) => {
    if (!group.items || group.items.length === 0) return null;

    // Add instance suffix to field names if this is a duplicated group
    const getFieldName = (baseName: string) => {
      return instanceId ? `${baseName}_${instanceId}` : baseName;
    };

    return (
      <div className="uipm:w-full">
        {/* Only show title on original group, not duplicates */}
        {group.title && !isDuplicate && (
          <h4 className="uipm:heading-4 uipm:mb-[25px]">{group.title}</h4>
        )}
        <div
          {...group}
          className="uipm:grid uipm:grid-cols-1 form-600:uipm:grid-cols-2 uipm:gap-[var(--container-gutter)] uipm:w-full"
        >
          {group.items.map((field) => {
            const fieldName = getFieldName(field.name);
            return (
              <FormField
                key={fieldName}
                field={{ ...field, name: fieldName }}
                formValues={formValues}
                formErrors={formErrors}
                phoneData={phoneData}
                inputBackgroundColor={inputBackgroundColor}
                onFieldChange={onFieldChange}
                onPhoneChange={onPhoneChange}
                onFieldFocus={onFieldFocus}
                onFieldBlur={onFieldBlur}
              />
            );
          })}
        </div>
        {/* Show Add More button only on original group */}
        {group.showAddMoreButton && onAddMore && !isDuplicate && (
          <div className="uipm:mt-[var(--container-gutter)]">
            <Button
              variant="secondary"
              size="medium"
              onClick={onAddMore}
              type="button"
            >
              Add More
            </Button>
          </div>
        )}
        {/* Show Remove button on duplicates */}
        {isDuplicate && onRemove && (
          <div className="uipm:mt-[var(--container-gutter)]">
            <Button
              variant="secondary"
              size="medium"
              onClick={onRemove}
              type="button"
            >
              Remove
            </Button>
          </div>
        )}
      </div>
    );
  },
);

FormField.displayName = "FormField";

FormFieldGroup.displayName = "FormFieldGroup";

type FormErrors = Record<string, string | null>;

type PhoneNumberOption = SelectOption & {
  numberLength?: number;
  validationMessage?: string;
};

type FieldCommon = {
  name: string;
  type: string;
  required: boolean;
  requiredErrorText: string;
  validations: Array<{ regularExpression: string; errorMessage: string }>;
  _editable?: string;
};

type WithCommonFields<T> = T & FieldCommon;

export type ContactFormField = WithCommonFields<{
  label?: string;
  placeholder?: string;
  options?: SelectOption[];
}>;

export type ContactFormInputGroup = {
  component: "inputGroup";
  title?: string;
  showAddMoreButton?: boolean;
  items?: ContactFormField[];
  _uid: string;
  _editable?: string;
};

export type ContactFormItem = ContactFormField | ContactFormInputGroup;

export type ContactSectionProps = VariantProps<typeof sectionWrapper> & {
  mode: "default" | "bright";
  head: SectionTitleProps | null;
  image: ImageWithAspectRatioProps | null;
  inputBackgroundColor?: "default" | "white";
  formBackgroundColor?: "default" | "gray";
  embedded?: boolean;
  form: {
    fields: ContactFormItem[] | null;
    button: ButtonProps | null;
    isCaptchaEnabled?: boolean;
    privacyPolicyText: React.ReactNode;
    privacyPolicyErrorText: string;
  };
  formSubmitTracking?: ({
    section,
    formName,
    formValue,
    ...rest
  }: {
    section: string;
    formName: string;
    formValue: number;
    [key: string]: string | number;
  }) => void;
  successScreen: React.ComponentType<{ onButtonClick: () => void }> | null;
  onSubmit?: (
    formData: Record<string, string | number>,
  ) => Promise<void | { success: boolean }>;
  trackFormSubmit?: ({
    formValue,
    ...rest
  }: {
    formValue: number;
    [key: string]: string | number;
  }) => void;
  trackFormStart?: () => void;
  trackFormInputClick?: (fieldName?: string) => void;
  trackFormError?: (
    errorType: string,
    errorMessage: string,
    fieldName?: string,
  ) => void;
  onTermsAgreementChange?: (agreement: boolean) => void;
} & SectionProps;

// const RecaptchaContainer = ({
//   hasError,
//   className,
//   id,
//   ...props
// }: {
//   hasError?: boolean;
//   className?: string;
//   id: string;
// }) => (
//   <div
//     className={cn(
//       "uipm:relative uipm:w-fit",
//       hasError &&
//         "uipm:rounded uipm:border-2 uipm:border-[var(--color-color-danger)]",
//       className
//     )}
//     id={id}
//     {...props}
//   />
// );

export function ContactSection({
  mode,
  head,
  image,
  inputBackgroundColor = "default",
  formBackgroundColor = "default",
  embedded = false,
  form,
  successScreen,
  onSubmit,
  className,
  trackFormSubmit,
  trackFormStart,
  trackFormInputClick,
  trackFormError,
  onTermsAgreementChange,
  backgroundColor = "none",
  topPadding = "none",
  bottomPadding = "none",
  ...props
}: ContactSectionProps) {
  const formStartTracked = useRef(false);
  const pageVisibleTimestamp = useRef(0);
  const isSubmittingRef = useRef(false);
  const SuccessScreenComponent = successScreen;

  useEffect(() => {
    const updateFocusTimestamp = () => {
      pageVisibleTimestamp.current = Date.now();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        updateFocusTimestamp();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", updateFocusTimestamp);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", updateFocusTimestamp);
    };
  }, []);
  // const isCaptchaEnabled = form.isCaptchaEnabled;
  const privacyPolicyText = form.privacyPolicyText;

  const [formValues, setFormValues] = useState<Record<string, string | number>>(
    {},
  );
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // const [hasErrorRecaptcha, setHasErrorRecaptcha] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [hasErrorCheckbox, setHasErrorCheckbox] = useState(false);

  const [phoneData, setPhoneData] = useState<
    Record<string, { countryCode: string; phoneNumber: string }>
  >({});

  // Track duplicated input groups: groupId -> array of instance IDs
  const [duplicatedGroups, setDuplicatedGroups] = useState<
    Record<string, string[]>
  >({});

  // const captchaId = useId();
  // const widgetIdRef = useRef<string | null>(null);
  // const initializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  // const [isRecaptchaScriptLoaded, setIsRecaptchaScriptLoaded] = useState(false);
  // const [, setIsRecaptchaInitialized] = useState(false);

  // const handleRecaptchaSuccess = useCallback((token: string) => {
  //   setRecaptchaToken(token);
  //   setHasErrorRecaptcha(false);
  // }, []);

  // const handleRecaptchaExpired = useCallback(() => {
  //   setRecaptchaToken(null);
  //   setHasErrorRecaptcha(false);
  // }, []);

  // const handleRecaptchaError = useCallback(() => {
  //   setRecaptchaToken(null);
  //   setHasErrorRecaptcha(true);
  // }, []);

  // const checkRecaptchaScriptLoaded = useCallback((): boolean => {
  //   return !!window.grecaptcha && !!window.grecaptcha.enterprise;
  // }, []);

  // const initRecaptcha = useCallback(() => {
  //   if (!isCaptchaEnabled || typeof window === "undefined") return;

  //   const captchaContainer = document.getElementById(captchaId);
  //   if (!captchaContainer) return;

  //   if (!checkRecaptchaScriptLoaded()) {
  //     if (initializationTimeoutRef.current) {
  //       clearTimeout(initializationTimeoutRef.current);
  //     }
  //     initializationTimeoutRef.current = setTimeout(initRecaptcha, 200);
  //     return;
  //   }

  //   try {
  //     if (captchaContainer.children.length > 0) {
  //       return;
  //     }

  //     captchaContainer.innerHTML = "";

  //     if (widgetIdRef.current !== null) {
  //       try {
  //         window.grecaptcha?.enterprise?.reset(widgetIdRef.current);
  //       } catch (error) {
  //         console.warn("Failed to reset previous reCAPTCHA widget:", error);
  //       }
  //       widgetIdRef.current = null;
  //     }

  //     window.grecaptcha?.enterprise?.ready(() => {
  //       try {
  //         if (window.grecaptcha?.enterprise && widgetIdRef.current === null) {
  //           widgetIdRef.current = window.grecaptcha.enterprise.render(
  //             captchaId,
  //             {
  //               sitekey: process.env.NEXT_PUBLIC_RECAPTCHA_KEY,
  //               callback: handleRecaptchaSuccess,
  //               "expired-callback": handleRecaptchaExpired,
  //               "error-callback": handleRecaptchaError,
  //             }
  //           );
  //         }
  //         setIsRecaptchaInitialized(true);
  //       } catch (error) {
  //         console.error("Error rendering reCAPTCHA:", error);
  //         setHasErrorRecaptcha(true);
  //       }
  //     });
  //   } catch (error) {
  //     console.error("Error initializing reCAPTCHA:", error);
  //     setHasErrorRecaptcha(true);
  //   }
  // }, [
  //   isCaptchaEnabled,
  //   captchaId,
  //   handleRecaptchaSuccess,
  //   handleRecaptchaExpired,
  //   handleRecaptchaError,
  //   checkRecaptchaScriptLoaded,
  // ]);

  // useEffect(() => {
  //   if (!isCaptchaEnabled) return;

  //   const initTimeout = setTimeout(() => {
  //     if (isRecaptchaScriptLoaded || checkRecaptchaScriptLoaded()) {
  //       initRecaptcha();
  //     }
  //   }, 100);

  //   return () => {
  //     clearTimeout(initTimeout);
  //     if (initializationTimeoutRef.current) {
  //       clearTimeout(initializationTimeoutRef.current);
  //     }
  //   };
  // }, [
  //   isCaptchaEnabled,
  //   isRecaptchaScriptLoaded,
  //   initRecaptcha,
  //   checkRecaptchaScriptLoaded,
  // ]);

  // useEffect(() => {
  //   return () => {
  //     if (widgetIdRef.current !== null && typeof window !== "undefined") {
  //       try {
  //         if (window.grecaptcha && window.grecaptcha.enterprise) {
  //           window.grecaptcha.enterprise.reset(widgetIdRef.current);
  //         }
  //       } catch (error) {
  //         console.error("Error resetting reCAPTCHA:", error);
  //       }
  //       widgetIdRef.current = null;
  //     }

  //     if (initializationTimeoutRef.current) {
  //       clearTimeout(initializationTimeoutRef.current);
  //     }

  //     const captchaContainer = document.getElementById(captchaId);
  //     if (captchaContainer) {
  //       captchaContainer.innerHTML = "";
  //     }
  //   };
  // }, [captchaId]);

  const validateField = (
    field: ContactFormField,
    value: string,
  ): string | null => {
    if (field.required && (!value || value.trim() === "")) {
      return field.requiredErrorText;
    }

    if (field.validations && value) {
      for (const validation of field.validations) {
        try {
          const regex = new RegExp(validation.regularExpression);

          let testValue = value;
          if (field.type === "tel") {
            const phoneInfo = phoneData[field.name!];
            if (phoneInfo) {
              testValue = phoneInfo.phoneNumber;
            }
          }

          if (!regex.test(testValue)) {
            return validation.errorMessage;
          }
        } catch {
          console.error("Invalid regex pattern:", validation.regularExpression);
        }
      }
    }

    if (field.type === "tel") {
      const phoneInfo = phoneData[field.name!];
      if (phoneInfo) {
        const testPhoneNumber = phoneInfo.phoneNumber;

        const selectedCountry = field.options?.find(
          (option) => option.value === phoneInfo.countryCode,
        ) as PhoneNumberOption | undefined;

        const allowedLength = selectedCountry?.numberLength;

        if (
          allowedLength &&
          testPhoneNumber &&
          testPhoneNumber.length !== allowedLength
        ) {
          return (
            selectedCountry?.validationMessage || "Invalid phone number length"
          );
        }
      }
    }

    return null;
  };

  const handleFieldFocus = (fieldName: string) => {
    if (isSubmittingRef.current) {
      return;
    }

    if (Date.now() - pageVisibleTimestamp.current < VISIBILITY_DEBOUNCE_MS) {
      return;
    }

    if (!formStartTracked.current && trackFormStart) {
      trackFormStart();
      formStartTracked.current = true;
    }

    if (trackFormInputClick) {
      trackFormInputClick(fieldName);
    }
  };

  const handleFieldChange = (fieldName: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    if (formErrors[fieldName]) {
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: null,
      }));
    }
  };

  const handlePhoneChange = (
    fieldName: string,
    countryCode: string,
    phoneNumber: string,
  ) => {
    setPhoneData((prev) => ({
      ...prev,
      [fieldName]: { countryCode, phoneNumber },
    }));

    const fullPhoneValue =
      countryCode && phoneNumber ? `${countryCode}${phoneNumber}` : "";
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: fullPhoneValue,
    }));

    if (formErrors[fieldName]) {
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: null,
      }));
    }
  };

  const handleFieldBlur = (fieldName: string) => {
    const field = findFieldByName(form.fields, fieldName);
    if (!field) return;

    const value = String(formValues[fieldName] || "");
    const error = validateField(field, value);

    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  const handleAddMoreGroup = (groupId: string) => {
    const instanceId = Date.now().toString();
    setDuplicatedGroups((prev) => ({
      ...prev,
      [groupId]: [...(prev[groupId] || []), instanceId],
    }));
  };

  const handleRemoveGroup = (groupId: string, instanceId: string) => {
    setDuplicatedGroups((prev) => ({
      ...prev,
      [groupId]: (prev[groupId] || []).filter((id) => id !== instanceId),
    }));

    // Remove form values for this instance
    const group = form.fields?.find(
      (item) => "component" in item && item._uid === groupId,
    ) as ContactFormInputGroup | undefined;

    if (group?.items) {
      const fieldsToRemove = group.items.map(
        (field) => `${field.name}_${instanceId}`,
      );
      setFormValues((prev) => {
        const newValues = { ...prev };
        fieldsToRemove.forEach((fieldName) => delete newValues[fieldName]);
        return newValues;
      });
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        fieldsToRemove.forEach((fieldName) => delete newErrors[fieldName]);
        return newErrors;
      });
      setPhoneData((prev) => {
        const newPhoneData = { ...prev };
        fieldsToRemove.forEach((fieldName) => delete newPhoneData[fieldName]);
        return newPhoneData;
      });
    }
  };

  const findFieldByName = (
    fields: ContactFormItem[] | null,
    fieldName: string,
  ): ContactFormField | null => {
    if (!fields) return null;

    for (const item of fields) {
      if ("name" in item && item.name === fieldName) {
        return item;
      }
      if (
        "component" in item &&
        item.component === "inputGroup" &&
        item.items
      ) {
        for (const field of item.items) {
          if (field.name === fieldName) {
            return field;
          }
        }
      }
    }
    return null;
  };

  const getAllFields = (
    fields: ContactFormItem[] | null,
  ): ContactFormField[] => {
    if (!fields) return [];

    const allFields: ContactFormField[] = [];
    for (const item of fields) {
      if ("name" in item) {
        allFields.push(item);
      } else if (
        "component" in item &&
        item.component === "inputGroup" &&
        item.items
      ) {
        allFields.push(...item.items);
      }
    }
    return allFields;
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    const allFields = getAllFields(form.fields);
    allFields.forEach((field) => {
      if (field.name) {
        const value = String(formValues[field.name] || "");
        const error = validateField(field, value);

        if (error) {
          errors[field.name] = error;
          isValid = false;
        }
      }
    });

    if (!isValid) {
      trackFormSubmit?.({
        formValue: 0,
        ...JSON.parse(JSON.stringify(formValues)),
      });

      if (trackFormError) {
        const errorEntries = Object.entries(errors).filter(
          ([, msg]) => msg !== null,
        );
        if (errorEntries.length > 0) {
          const [firstFieldName, firstErrorMessage] = errorEntries[0]!;
          const errorType = firstErrorMessage!.includes("required")
            ? "missing_field"
            : "validation_error";
          trackFormError(
            errorType,
            errorEntries.length > 1
              ? `${errorEntries.length} fields have errors. First: ${firstFieldName} - ${firstErrorMessage}`
              : `${firstFieldName}: ${firstErrorMessage}`,
          );
        }
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const transformFormValuesForSubmission = (
    values: Record<string, string | number>,
  ): Record<string, string | number | unknown[]> => {
    const result: Record<string, string | number | unknown[]> = {};
    const groupArrays: Record<string, Record<string, string | number>[]> = {};

    // Build a map of group fields for quick lookup
    const groupFieldMap = new Map<
      string,
      { groupKey: string; isOriginal: boolean }
    >();

    form.fields?.forEach((item) => {
      if (
        "component" in item &&
        item.component === "inputGroup" &&
        item.showAddMoreButton &&
        item.items
      ) {
        const groupKey = item.title || item._uid;
        item.items.forEach((field) => {
          groupFieldMap.set(field.name, { groupKey, isOriginal: true });
        });
      }
    });

    // Process all form values
    Object.entries(values).forEach(([key, value]) => {
      const match = key.match(/^(.+)_(\d+)$/);

      if (match) {
        // Duplicated field: fieldName_instanceId
        const [, baseName, instanceId] = match;

        if (!baseName) return;

        const groupInfo = groupFieldMap.get(baseName);

        if (groupInfo) {
          const { groupKey } = groupInfo;
          if (!groupArrays[groupKey]) {
            groupArrays[groupKey] = [];
          }

          // Find or create the instance object
          let instanceObj = groupArrays[groupKey].find((obj) => {
            const objInstanceId = (
              obj as Record<string, string | number> & { __instanceId?: string }
            ).__instanceId;
            return objInstanceId === instanceId;
          });
          if (!instanceObj) {
            instanceObj = { __instanceId: instanceId } as Record<
              string,
              string | number
            > & { __instanceId: string };
            groupArrays[groupKey].push(instanceObj);
          }
          instanceObj[baseName] = value;
        }
      } else {
        // Original field
        const groupInfo = groupFieldMap.get(key);

        if (groupInfo) {
          // Original instance of a duplicatable group
          const { groupKey } = groupInfo;
          if (!groupArrays[groupKey]) {
            groupArrays[groupKey] = [];
          }
          if (!groupArrays[groupKey][0]) {
            groupArrays[groupKey][0] = {};
          }
          groupArrays[groupKey][0][key] = value;
        } else {
          // Regular non-group field
          result[key] = value;
        }
      }
    });

    // Clean up and add group arrays to result
    Object.entries(groupArrays).forEach(([groupKey, instances]) => {
      result[groupKey] = instances.map((instance) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { __instanceId, ...fields } = instance as Record<
          string,
          string | number
        > & { __instanceId?: string };
        return fields;
      });
    });

    return result;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();

    isSubmittingRef.current = true;

    setTimeout(() => {
      isSubmittingRef.current = false;
    }, 500);

    setIsSubmitting(true);

    const isFormValid = validateForm();

    // let isRecaptchaValid = true;
    // if (isCaptchaEnabled) {
    //   if (!recaptchaToken) {
    //     setHasErrorRecaptcha(true);
    //     isRecaptchaValid = false;
    //   } else {
    //     setHasErrorRecaptcha(false);
    //   }
    // }

    const isCheckboxValid = isCheckboxChecked;
    if (!isCheckboxValid) {
      setHasErrorCheckbox(true);

      if (trackFormError) {
        trackFormError(
          "checkbox_error",
          form.privacyPolicyErrorText ||
            "Please agree to the Terms & Conditions",
        );
      }
    }

    if (!isFormValid || !isCheckboxValid) {
      setIsSubmitting(false);
      return;
    }

    setHasErrorCheckbox(false);

    try {
      // Transform form values to handle duplicated groups
      const transformedValues = transformFormValuesForSubmission(formValues);

      // const withRecaptcha: Record<string, string | number | unknown[]> = {
      //   "g-recaptcha-response": recaptchaToken || "",
      //   ...transformedValues,
      // };

      try {
        const result = onSubmit?.(
          transformedValues as Record<string, string | number>,
        );
        if (result && typeof result.then === "function") {
          result
            .then(() => {
              setFormValues({});
              setPhoneData({});
              setFormErrors({});
              // setRecaptchaToken(null);
              setIsSubmitted(true);
            })
            .catch((error: unknown) => {
              console.error("Form submission failed:", error);
            });
        } else {
          setFormValues({});
          setPhoneData({});
          setFormErrors({});
          // setRecaptchaToken(null);
          setIsSubmitted(true);
        }
      } catch (error) {
        console.error("Form submission failed:", error);
      }

      // if (
      //   isCaptchaEnabled &&
      //   widgetIdRef.current !== null &&
      //   window.grecaptcha
      // ) {
      //   try {
      //     window.grecaptcha?.enterprise?.reset(widgetIdRef.current);
      //   } catch (error) {
      //     console.warn("Error resetting reCAPTCHA after submit:", error);
      //   }
      // }
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitted(true);
      setIsSubmitting(false);
    }
  };

  const formBlock = (
    <>
      <form onSubmit={handleSubmit} className="uipm:w-full">
        <div
          className={cn(
            "uipm:flex uipm:flex-col uipm:gap-[var(--container-gutter)] uipm:w-full",
            formBackgroundColor === "gray" &&
              "uipm:bg-[var(--surface-surface-secondary)]",
          )}
        >
          {form.fields?.map((item: ContactFormItem) => {
            if ("name" in item) {
              return (
                <FormField
                  key={item.name}
                  field={item}
                  formValues={formValues}
                  formErrors={formErrors}
                  phoneData={phoneData}
                  inputBackgroundColor={inputBackgroundColor}
                  onFieldChange={handleFieldChange}
                  onPhoneChange={handlePhoneChange}
                  onFieldFocus={handleFieldFocus}
                  onFieldBlur={handleFieldBlur}
                />
              );
            } else if (item.component === "inputGroup") {
              const groupInstances = duplicatedGroups[item._uid] || [];
              return (
                <React.Fragment key={item._uid}>
                  {/* Original group */}
                  <FormFieldGroup
                    group={item}
                    formValues={formValues}
                    formErrors={formErrors}
                    phoneData={phoneData}
                    inputBackgroundColor={inputBackgroundColor}
                    onFieldChange={handleFieldChange}
                    onPhoneChange={handlePhoneChange}
                    onFieldFocus={handleFieldFocus}
                    onFieldBlur={handleFieldBlur}
                    onAddMore={
                      item.showAddMoreButton
                        ? () => handleAddMoreGroup(item._uid)
                        : undefined
                    }
                  />
                  {/* Duplicated instances */}
                  {groupInstances.map((instanceId) => (
                    <FormFieldGroup
                      key={`${item._uid}_${instanceId}`}
                      group={item}
                      instanceId={instanceId}
                      isDuplicate={true}
                      formValues={formValues}
                      formErrors={formErrors}
                      phoneData={phoneData}
                      inputBackgroundColor={inputBackgroundColor}
                      onFieldChange={handleFieldChange}
                      onPhoneChange={handlePhoneChange}
                      onFieldFocus={handleFieldFocus}
                      onFieldBlur={handleFieldBlur}
                      onRemove={() => handleRemoveGroup(item._uid, instanceId)}
                    />
                  ))}
                </React.Fragment>
              );
            }
            return null;
          })}
          <div className="uipm:flex uipm:flex-col uipm:gap-2">
            <div className="uipm:flex uipm:flex-col uipm:gap-2">
              <div className="uipm:flex uipm:items-center uipm:justify-center uipm:gap-2">
                <Checkbox
                  state={hasErrorCheckbox ? "error" : "default"}
                  checked={isCheckboxChecked}
                  onChange={() => {
                    setIsCheckboxChecked((checked) => {
                      const newValue = !checked;
                      if (newValue) {
                        setHasErrorCheckbox(false);
                      }
                      if (onTermsAgreementChange) {
                        onTermsAgreementChange(newValue);
                      }
                      return newValue;
                    });
                  }}
                />
                <div className="uipm:privacy-policy-text uipm:flex-1">
                  <span className="uipm:flex-1 uipm:font-body uipm:text-(length:--typography-sizes-body-medium) uipm:leading-[1.35] uipm:text-[var(--typography-colors-text-faded-dark)]!">
                    {privacyPolicyText}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* {isCaptchaEnabled && (
            <>
              <Script
                strategy="afterInteractive"
                src="https://www.google.com/recaptcha/enterprise.js?render=explicit"
                onLoad={() => {
                  setIsRecaptchaScriptLoaded(true);
                }}
                onError={() => {
                  console.error("Failed to load reCAPTCHA script");
                }}
              />
              <RecaptchaContainer
                key={"g-recaptcha"}
                className="g-recaptcha"
                id={captchaId}
                hasError={hasErrorRecaptcha}
              />
            </>
          )} */}

          {form.button && (
            <Button
              {...form.button}
              className={"uipm:self-start"}
              type="submit"
              disabled={isSubmitting}
            />
          )}
        </div>
      </form>
    </>
  );

  if (embedded) {
    return isSubmitted && SuccessScreenComponent ? (
      <SuccessScreenComponent onButtonClick={() => setIsSubmitted(false)} />
    ) : (
      <div className="uipm:w-full">
        {head && (
          <SectionTitleMemo
            {...head}
            className="uipm:mb-[var(--spacing-primitives-32)]"
          />
        )}
        {formBlock}
      </div>
    );
  }

  return (
    <div
      className={sectionWrapper({ backgroundColor, topPadding, bottomPadding })}
    >
      {isSubmitted && SuccessScreenComponent ? (
        <SuccessScreenComponent onButtonClick={() => setIsSubmitted(false)} />
      ) : (
        <>
          <Section
            {...props}
            className={cn(
              {
                "ui:brightMode": mode === "bright",
              },
              className,
            )}
            defaultPaddingBottom={
              bottomPadding === "none" ? undefined : "uipm:pb-12"
            }
          >
            <Container
              className={cn(
                "uipm:flex uipm:flex-col uipm:gap-[var(--spacing-primitives-48)] uipm:items-center uipm:justify-center",
                image && "uipm:md:flex-row",
                {
                  "uipm:px-[var(--container-gutter)] uipm:py-[var(--spacing-primitives-64)]":
                    formBackgroundColor === "default",
                },
              )}
            >
              <Box
                variant={image ? "half" : "full"}
                className={
                  "uipm:flex uipm:flex-col uipm:gap-[var(--spacing-primitives-32)]"
                }
              >
                {head && <SectionTitleMemo {...head} />}
                {formBlock}
              </Box>

              {image && (
                <Box
                  variant={"half"}
                  className="uipm:flex uipm:items-center uipm:justify-center"
                >
                  <div
                    className="uipm:w-full uipm:max-w-[420px] uipm:max-h-[600px] uipm:relative"
                    style={{
                      aspectRatio: image.aspectRatio || undefined,
                      minHeight: "600px",
                    }}
                  >
                    <Image
                      src={image.image?.src || ""}
                      alt={parseAiAlt(image.image?.alt).cleanAlt}
                      fill
                      style={{ objectFit: "contain" }}
                      sizes="(max-width: 768px) 100vw, 50vw"
                    />
                    <AiDisclaimerForAlt
                      alt={image.image?.alt}
                      src={
                        typeof image.image?.src === "string"
                          ? image.image.src
                          : null
                      }
                    />
                  </div>
                </Box>
              )}
            </Container>
          </Section>
        </>
      )}
    </div>
  );
}
